import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SDropdownV3 } from "../../SDropdownV3";
import { SInputV2 } from "../../SInputV2";
import { SSlider } from "../../SSlider";
import { useCommandKey } from "../../hooks";
const units = [
    "px",
    "%",
    "vw",
    "vh",
    "rem",
    "em"
];
const globalUnits = [
    "auto"
];
const placeholderUnits = [
    "none"
];
const valueTypes = [
    ...units,
    ...globalUnits,
    ...placeholderUnits
];
export const CSSLengthEditor = (props)=>{
    const { value, label } = props;
    // We know now that literalValue is `css-length` or `css-string`
    const getNextState = (currentState, action)=>{
        const literalValue = currentState.value;
        const literValueIsRaw = literalValue?.unit === "raw";
        const literValueIsGlobal = literalValue?.unit == "none";
        const literalValueHasUnit = literalValue && literalValue?.unit !== "raw" && literalValue?.unit !== "none";
        switch(action.type){
            case "KEY_DOWN":
                {
                    const { event } = action;
                    const key = event.key;
                    const length = literalValue?.value;
                    if (length?.includes("calc") || length?.includes("var")) {
                        return {
                            value: {
                                type: "css-length",
                                unit: "raw",
                                value: length
                            }
                        };
                    }
                    const KeyMap = {
                        ArrowUp: event.shiftKey ? 10 : 1,
                        ArrowDown: event.shiftKey ? -10 : -1
                    };
                    if (key in KeyMap) {
                        const parsedLength = parseFloat(length || "0");
                        const newValue = isNaN(parsedLength) ? 0 : parsedLength + KeyMap[key];
                        if (!literValueIsGlobal) {
                            return {
                                value: {
                                    type: "css-length",
                                    unit: literalValue?.unit || "px",
                                    value: newValue.toString()
                                }
                            };
                        } else {
                            return {
                                value: {
                                    type: "css-length",
                                    unit: "px",
                                    value: newValue.toString()
                                }
                            };
                        }
                    }
                    break;
                }
            case "DROPDOWN_SELECT":
                {
                    const { unit } = action;
                    if (globalUnits.includes(unit)) {
                        return {
                            value: {
                                type: "css-length",
                                value: unit,
                                // Idk why this isn't validating
                                unit: "none"
                            }
                        };
                    }
                    return {
                        value: {
                            type: "css-length",
                            value: (literValueIsGlobal ? "" : literalValue?.value) || "",
                            unit: unit
                        }
                    };
                }
            case "INPUT_CHANGE":
                {
                    const { value } = action;
                    const literalValue = currentState.value;
                    let length = literalValue?.value;
                    let unit = literalValue?.type === "css-length" ? literalValue.unit === "raw" ? null : literalValue.unit : null;
                    // Allow for calc & vars `calc(var(--width) + 10px)`
                    if (value.includes("calc") || value.includes("var")) {
                        return {
                            value: {
                                type: "css-length",
                                unit: "raw",
                                value: value
                            }
                        };
                    }
                    // Snap to units, `12px` -> `12` `px`,
                    // `auto` -> `auto` `none`
                    for (let type of valueTypes){
                        if (value.includes(type)) {
                            unit = type;
                            const parsedLength = value.match(/[a-zA-Z]+|[0-9\.-0-9]+|[%]+/g);
                            if (parsedLength && parsedLength[0]) {
                                if (globalUnits.includes(type)) {
                                    unit = "none";
                                    length = type;
                                } else {
                                    length = parsedLength[0];
                                }
                                return {
                                    value: {
                                        type: "css-length",
                                        value: length,
                                        unit: unit
                                    }
                                };
                            }
                        }
                    }
                    // We already have a unit, so just update the value
                    // this could also result in a weird value but that's
                    // ok for now
                    if (literalValueHasUnit) {
                        return {
                            value: {
                                type: "css-length",
                                value: value,
                                unit: literalValue?.unit
                            }
                        };
                    }
                    // This may result in a weird value if you're typing 'aut' 'px'
                    // See if we can parse the value as a number
                    const parsedValue = parseFloat(value);
                    if (!isNaN(parsedValue)) {
                        return {
                            value: {
                                type: "css-length",
                                value: value,
                                // Picking up the default unit
                                unit: "px"
                            }
                        };
                    }
                    // Leave this as raw to indicate
                    // that we don't know what the value is
                    return {
                        value: {
                            type: "css-length",
                            value: value,
                            unit: "raw"
                        }
                    };
                }
        }
        return {
            value: null
        };
    };
    const dispatch = useCallback((action)=>{
        const nextState = getNextState({
            value: value || null
        }, action);
        if (nextState.value) {
            props.onChangeValue && props.onChangeValue(nextState.value);
        }
    }, [
        getNextState,
        value
    ]);
    const { isCommandPressed } = useCommandKey();
    const [sliderBounds, setSliderBounds] = useState(undefined);
    const [hovered, setHovered] = useState(false);
    const [mouseDown, setMouseDown] = useState(false);
    useEffect(()=>{
        if (isCommandPressed && (hovered || mouseDown)) {
            setSliderBounds({
                min: 0,
                max: parseInt(value?.value || "0") * 3
            });
        }
    }, [
        isCommandPressed,
        hovered,
        mouseDown,
        setSliderBounds
    ]);
    const [isFocused, setIsFocused] = useState(false);
    const showSlider = useMemo(()=>{
        return isCommandPressed && (hovered || mouseDown) && !isFocused;
    }, [
        isCommandPressed,
        hovered,
        mouseDown,
        isFocused
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        onMouseDown: (e)=>{
            setMouseDown(true);
        },
        onMouseUp: (e)=>{
            setMouseDown(false);
        },
        onMouseLeave: (e)=>{
            setHovered(false);
        },
        onMouseEnter: (e)=>{
            setHovered(true);
        },
        className: `relative bg-grayscale-dark rounded-md border border-white/10 grid overflow-hidden ${showSlider ? "grid-cols-[auto,1fr,auto] z-[10]" : "grid-cols-[1fr,auto]"} items-center`,
        children: [
            showSlider && /*#__PURE__*/ _jsx("div", {
                className: "px-3 left-0 absolute right-0 top-0 bottom-0 bg-grayscale-dark z-10",
                children: /*#__PURE__*/ _jsx(SSlider, {
                    className: "!h-[30px] !w-full",
                    value: parseInt(value?.value || "0"),
                    onChange: (e)=>dispatch({
                            type: "INPUT_CHANGE",
                            value: e.toString()
                        }),
                    min: sliderBounds?.min || 0,
                    max: sliderBounds?.max || 100,
                    step: 1
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-row items-center",
                children: [
                    label && /*#__PURE__*/ _jsx("div", {
                        className: "text-xs opacity-50 px-[6px] flex items-center justify-center border-r border-white/20 h-[30px] ",
                        children: label
                    }),
                    /*#__PURE__*/ _jsx(SInputV2, {
                        borderless: true,
                        tabIndex: 1,
                        className: `text-[13px] !pl-2 !pr-0 !h-[30px] !py-0 `,
                        value: value?.value || "",
                        placeholder: "—",
                        onFocus: (e)=>{
                            setIsFocused(true);
                        },
                        onBlur: (e)=>{
                            setIsFocused(false);
                        },
                        onChange: (e)=>dispatch({
                                type: "INPUT_CHANGE",
                                value: e.target.value
                            }),
                        onKeyDown: (event)=>dispatch({
                                type: "KEY_DOWN",
                                event
                            })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SDropdownV3, {
                tabIndex: -1,
                options: valueTypes.filter((e)=>e != "none").map((unit)=>{
                    return {
                        label: unit,
                        value: unit
                    };
                }),
                value: {
                    value: value?.unit,
                    label: value?.unit
                },
                showIcon: false,
                placeholder: "–",
                onChange: (option)=>dispatch({
                        type: "DROPDOWN_SELECT",
                        unit: option.value
                    }),
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-[13px] opacity-70 px-2",
                    children: value?.unit === "none" ? "-" : value?.unit || "px"
                })
            })
        ]
    });
};
